import { gsap } from "gsap";

import { EventsManager } from "../../../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../../../abstracts/flow/AbstractGameMode";
import { AbstractSpine } from "../../../../../abstracts/graphics/AbstractSpine";
import { AbstractView } from "../../../../../abstracts/modules/AbstractView";
import { AbstractScreen } from "../../../../../abstracts/modules/screens/AbstractScreen";
import { ScreenProperties } from "../../../../../abstracts/types/interfaces";
import { getScreenProperties } from "../../../../../abstracts/utils/JSUtils";
import {
    fadeAndStopSound,
    fadeOutBackgroundMusic,
    fadeSound,
    playNextBackgroundMusic,
    playSound,
    startAmbSounds,
} from "../../../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../../../Apocalypse";
import {
    ApocalypseSoundNames,
    ApocalypseSpineNames,
} from "../../../../assetsLoader/ApocalypseAssetsNames";
import { SHOW_GREEN_BILLBOARD } from "../../../../events/ApocalypseEvents";
import { ApocalypseGameData, ApocalypseInitData } from "../../../../types/ApocalypseInterfaces";
import { stopRadioSound } from "../../../Sound/ApocalypseSoundController";
import { ApocalypseIntroText } from "../ApocalypseIntroText";

export class ApocalypseFreespinIntroScreen extends AbstractScreen {
    protected mainSpine: AbstractSpine;

    protected customText: ApocalypseIntroText;

    constructor(parentContainer: AbstractView, name: string = `ApocalypseFreespinIntroScreen`) {
        super(name, parentContainer);
    }

    protected init(): void {
        super.init();

        this.addBackgroundImage();
        this.addContinueButton();

        this.addSpinsTexts();
    }

    protected addBackgroundImage(): void {
        this.mainSpine = new AbstractSpine(ApocalypseSpineNames.FS_INTRO_OUTRO_DOORS);
        this.mainSpine.name = `fsDoor`;
        this.addChild(this.mainSpine);

        this.onOrientationChange();
    }

    protected addSpinsTexts(): void {
        const gameData: ApocalypseInitData | ApocalypseGameData = Apocalypse.model.data;

        const fsLeft: number = gameData.freespinsLeft;

        this.customText = new ApocalypseIntroText(fsLeft, false);
    }

    protected addContinueButton(): void {
        this.continueButton.addClickCallback(() => this.continueButtonClickCallback());
        this.continueButton.position.set(-this.continueButton.width / 2, 218); //! maybe other values if mobile
        this.continueButton.enable(true);
    }

    protected continueButtonClickCallback(): void {
        this.onContinueBtnClick();

        this.removeChild(this.continueButton);
        this.removeChild(this.customText);

        playSound({ name: ApocalypseSoundNames.CONTINUE_BUTTON });
        // playSound({ name: ApocalypseSoundNames.FS_ON_CONTINUE });
        startAmbSounds();

        // fadeAndStopSound({ name: this.getIntroLoopSoundName(), duration: 0.5, from: 1, to: 0 });
        if (!this.name.toLowerCase().includes(`outro`.toLowerCase())) {
            playNextBackgroundMusic();
        }

        if (this.name.toLowerCase().includes(`intro`)) {
            stopRadioSound();
        }
    }

    public override getGsapAnimation(
        fsLeft: number = 0,
        gameMode: AbstractGameMode = AbstractGameMode.FREESPIN
    ): gsap.core.Timeline {
        this.customText.updateSpinsLeftText(fsLeft);

        const tl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
            onStart: () => {
                this.addChild(this.customText);
                fadeOutBackgroundMusic(0.1);
            },
        });

        tl.add(
            this.mainSpine.getSpineTween(
                `intro`,
                false,
                () => {
                    playSound({ name: ApocalypseSoundNames.VAULT_DOOR_CLOSE });

                    gsap.delayedCall(0.2, () => {
                        if (
                            Apocalypse.model.data.mode === AbstractGameMode.SUPER_FREESPIN ||
                            Apocalypse.model.data.nextMode === AbstractGameMode.SUPER_FREESPIN
                        ) {
                            stopRadioSound();
                            playNextBackgroundMusic();

                            playSound({ name: ApocalypseSoundNames.SFS_RADIO_DISTURBANCE });

                            fadeSound({
                                name: ApocalypseSoundNames.SUPER_FREESPIN_MUSIC,
                                from: 0,
                                to: 0,
                                duration: 0,
                            });

                            playSound({ name: ApocalypseSoundNames.SFS_INTRO, repeat: 9999999 });
                        }
                    });
                },
                null,
                () => {
                    if (
                        Apocalypse.model.data.wasFeatureBuy &&
                        Apocalypse.autoPlayPlugin.isAutoplayRound
                    ) {
                        gsap.delayedCall(0.3, () => this.continueButtonClickCallback());
                    } else {
                        this.addChild(this.continueButton);
                    }
                }
            )
        );

        tl.add(this.customText.getAnimation(false, gameMode), 0.8);

        return tl;
    }

    public openDoors(): gsap.core.Timeline {
        const tl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
            onStart: () => {
                if (
                    Apocalypse.model.data.mode === AbstractGameMode.SUPER_FREESPIN ||
                    Apocalypse.model.data.nextMode === AbstractGameMode.SUPER_FREESPIN
                ) {
                    fadeAndStopSound({
                        name: ApocalypseSoundNames.SFS_INTRO,
                        from: 1,
                        to: 0,
                        duration: 2000,
                    });

                    fadeAndStopSound({
                        name: ApocalypseSoundNames.SFS_RADIO_DISTURBANCE,
                        from: 1,
                        to: 0,
                        duration: 2000,
                    });

                    if (Apocalypse.model.data.nextMode !== AbstractGameMode.NORMAL) {
                        fadeSound({
                            name: ApocalypseSoundNames.SUPER_FREESPIN_MUSIC,
                            from: 0,
                            to: 1,
                            duration: 2,
                        });
                    }
                }

                if (
                    Apocalypse.model.data.mode !== AbstractGameMode.NORMAL &&
                    Apocalypse.model.data.nextMode === AbstractGameMode.NORMAL
                ) {
                    EventsManager.trigger(SHOW_GREEN_BILLBOARD);
                }
            },
        });

        tl.add(
            this.mainSpine.getSpineTween(
                `outro`,
                false,
                () => {
                    playSound({ name: ApocalypseSoundNames.VAULT_DOOR_OPEN });
                },
                null,
                () => this.selfRemoveFromParent()
            )
        );

        return tl;
    }

    protected getIntroLoopSoundName(): string {
        return ApocalypseSoundNames.SFS_INTRO;
    }

    public override onOrientationChange(): void {
        super.onOrientationChange();

        const screenProps: ScreenProperties = getScreenProperties();
        let imageWidth: number = 838;
        let imageHeight: number = 1678;

        let scaleFactor: number;

        if (screenProps.isPortrait) {
            scaleFactor = screenProps.bounds.height / imageHeight;
        } else {
            scaleFactor = screenProps.bounds.width / 2 / imageWidth;
        }

        this.mainSpine.scale.set(scaleFactor);
    }
}
